import {Optional} from '@yandex-delivery/types'
import dayjs, {Dayjs} from 'dayjs'
import calendar from 'dayjs/plugin/calendar'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import dayOfYear from 'dayjs/plugin/dayOfYear'
import isBetweenPlugin from 'dayjs/plugin/isBetween'
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter'
import isSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import {constant, flow, isNil, isNumber, isString} from 'lodash/fp'
import {allPass, anyPass, ifElse} from 'ramda'

import {isFilled} from './index'
import {get} from './src'

dayjs.extend(isBetweenPlugin)
dayjs.extend(isSameOrAfterPlugin)
dayjs.extend(isSameOrBeforePlugin)
dayjs.extend(utc)
dayjs.extend(calendar)
dayjs.extend(timezone)
dayjs.extend(localizedFormat)
dayjs.extend(localeData)
dayjs.extend(customParseFormat)
dayjs.extend(dayOfYear)

const MOSCOW_OFFSET = 180
export const TIMEZONE_KEY = 'timezone'

export const MINUTES_IN_DAY = 60 * 24

export type TimeZone = {
  city: {
    name?: string
    id: number
  }
  country: {
    name?: string
    id: number
  }
  name: string
  utcOffset: number
}

const hasName = flow([get('name'), isString])
const hasNotName = flow([get('name'), isNil])
const hasOrNotName = anyPass([hasName, hasNotName])
const hasID = flow([get('id'), isNumber])
const hasOffset = flow([get('utcOffset'), isNumber])

const validateCity = flow(get('city'), ifElse(isFilled, allPass([hasName, hasID]), constant(true)))

const validateCountry = flow(get('country'), ifElse(isFilled, allPass([hasOrNotName, hasID]), constant(true)))

export const isValidTimeZone = (timeZone?: unknown): timeZone is TimeZone =>
  allPass([hasOffset, hasName, validateCity, validateCountry])(timeZone)

// !!!TODO killua перенести в куки https://st.yandex-team.ru/CARGODEV-12595
export const setLocalStorageTimeZone = (newTimeZone: TimeZone) =>
  window.localStorage.setItem(TIMEZONE_KEY, JSON.stringify(newTimeZone))

export const getLocalStorageTimeZone = (): TimeZone | undefined => {
  const item = window.localStorage.getItem(TIMEZONE_KEY)
  try {
    const parsedItem = !!item ? JSON.parse(item) : undefined

    return isValidTimeZone(parsedItem) ? parsedItem : undefined
  } catch (e) {
    return
  }
}

export const getTimeZoneCityName: (tz?: TimeZone) => string | undefined = get('city.name')
export const getTimeZoneUtcOffset: (tz?: TimeZone) => number | undefined = get('utcOffset')

let utcOffset = MOSCOW_OFFSET

export const getUtcOffset = () => utcOffset
export const setUtcOffset = (newOffset: number) => {
  if (!isNumber(newOffset)) {
    return
  }
  utcOffset = newOffset
}

const timeZone = getLocalStorageTimeZone()

export const dateWithTimeZone = (date?: string | number) => {
  if (!timeZone) {
    return dayjs(date).local().utcOffset(utcOffset)
  }
  return dayjs(date).utcOffset(timeZone.utcOffset)
}

export function sort(value1: Optional<number | string>, value2: Optional<number | string>): number {
  return dayjs(value1).diff(dayjs(value2))
}

export function toDate(value: Optional<number | string>, format?: string): Dayjs {
  return dayjs(value, format)
}

export function toUtc(date: Optional<string | number>): Dayjs {
  return dayjs.utc(date)
}

export function isSameOrAfter(value1: Dayjs, value2: Dayjs): boolean {
  return value1.isSameOrAfter(value2)
}

export function isSameOrBefore(value1: Dayjs, value2: Dayjs): boolean {
  return value1.isSameOrBefore(value2)
}

export function isBetween(value: Dayjs, start: Dayjs, end: Dayjs): boolean {
  return value.isBetween(start, end, undefined, '[]')
}
